"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";

interface VideoEmbedProps {
  title?: string;
  subtitle?: string;
  videoUrl: string;
}

/**
 * Converts various YouTube URL formats to an embeddable URL.
 */
function getEmbedUrl(url: string): string {
  // Already an embed URL
  if (url.includes("youtube.com/embed/")) return url;

  // Standard YouTube URL
  const standardMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (standardMatch) {
    return `https://www.youtube.com/embed/${standardMatch[1]}`;
  }

  // YouTube shorts
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
  if (shortsMatch) {
    return `https://www.youtube.com/embed/${shortsMatch[1]}`;
  }

  // Fallback: return as-is (could be a direct video URL or other embed)
  return url;
}

const VideoEmbed = ({ title, subtitle, videoUrl }: VideoEmbedProps) => {
  if (!videoUrl) return null;

  const embedUrl = getEmbedUrl(videoUrl);

  return (
    <section className="py-24 px-6 bg-white">
      <div className="container max-w-4xl mx-auto">
        {/* Header */}
        {(title || subtitle) && (
          <div className="text-center mb-12 space-y-4">
            {title && (
              <BlurFade delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
                  {title}
                </h2>
              </BlurFade>
            )}
            {subtitle && (
              <BlurFade delay={0.15}>
                <p className="text-foreground/60 text-lg max-w-2xl mx-auto">
                  {subtitle}
                </p>
              </BlurFade>
            )}
          </div>
        )}

        {/* Video */}
        <BlurFade delay={0.2}>
          <div className="relative w-full rounded-3xl overflow-hidden border border-foreground/5 shadow-lg bg-black aspect-video">
            <iframe
              src={embedUrl}
              title={title || "Video"}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default VideoEmbed;
